import { useMemo, useState } from "react";
import AddFeaturesPopupPresenter from "./AddFeaturesPopupPresenter";
import { FeaturesPopupData } from "api/interfaces/settingsInterface.interface";
import useStatusMutation from "hook/useStatusMutation";
import {
  CustomError,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { initialData } from "../FeaturesPopupContainer";
import {
  deleteFeaturesPopup,
  putFeaturesPopup,
  postFeaturesPopup,
} from "api/settingAPI";
import { checkEmpty, toTrim } from "utils/functions";

type Props = {
  onModal: () => void;
  onClickPreview: () => void;
  data: FeaturesPopupData;
  setData: React.Dispatch<React.SetStateAction<FeaturesPopupData>>;
};

const initialValidateMsg: ValidateMsg = {
  title: "",
};

export default function AddFeaturesPopupContainer(props: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const {
    handleErrorNotify,
    notifySuccess,
    lang,
    intlString,
    createCustomError,
  } = useApiError();
  const [deleteItem, setDeleteItem] = useState<string[]>([]);
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    ...initialValidateMsg,
  });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] =
    useState<boolean>(false);

  const onChangeFeatures = (key: string, value: string | boolean | Date) => {
    props.setData((prev) => ({ ...prev, [key]: value }));
  };
  const onChangeSlides = (index: number, key: string, value: string) => {
    props.setData({
      ...props.data,
      slides: props.data.slides
        .map((slide, i) => (i === index ? { ...slide, [key]: value } : slide))
        .sort((a, b) => a.slideOrder - b.slideOrder),
    });
  };
  const errorMsg: string = useMemo(
    () =>
      props.data.id
        ? intlString(lang.label.features.notify.update.fail)
        : intlString(lang.label.features.notify.add.fail),
    [intlString, lang, props.data.id]
  );
  const onClickAddSlide = () => {
    props.setData({
      ...props.data,
      slides: [
        ...props.data.slides,
        {
          title: "",
          url: "",
          description: "",
          slideOrder: props.data.slides.length + 1,
          id: new Date().getTime().toString(),
        },
      ],
    });
  };
  const createMutation = useStatusMutation(postFeaturesPopup, {
    onError: (error: CustomError) => {
      handleErrorNotify(error, errorMsg, "label.features.error");
    },
    onSuccess: (res: any) => {
      if (res.error !== 0 || !res.result) {
        handleErrorNotify(
          createCustomError(res, errorMsg),
          errorMsg,
          "label.features.error"
        );
        return;
      }
      notifySuccess(lang.label.features.notify.add.success);
      onClickClose();
    },
  });

  const updateMutation = useStatusMutation(putFeaturesPopup, {
    onError: (error: CustomError) => {
      handleErrorNotify(error, errorMsg, "label.features.error");
    },
    onSuccess: (res: any) => {
      if (res.error !== 0 || !res.result) {
        handleErrorNotify(
          createCustomError(res, errorMsg),
          errorMsg,
          "label.features.error"
        );
        return;
      }
      notifySuccess(lang.label.features.notify.update.success);
      onClickClose();
    },
  });
  const onClickPreview = () => {
    props.onClickPreview();
  };
  const onClickSave = () => {
    if (!createMutation.idle || !updateMutation.idle) return;
    setValidateMsg({
      ...initialValidateMsg,
    });
    if (checkEmpty(props.data.title)) {
      setValidateMsg((info) => {
        return {
          ...initialValidateMsg,
          title: "Please enter a title",
        };
      });
      return;
    }
    if (
      props.data.slides.filter((slide) => !checkEmpty(slide.title)).length === 0
    ) {
      setValidateMsg((info) => {
        return {
          ...initialValidateMsg,
          slides: "Please add at least one slide",
        };
      });
      return;
    }
    if (
      props.data.slides.some(
        (slide) =>
          checkEmpty(slide.title) ||
          checkEmpty(slide.url) ||
          checkEmpty(slide.description)
      )
    ) {
      setValidateMsg((info) => {
        return {
          ...initialValidateMsg,
          slides: "Please fill in all fields",
        };
      });
      return;
    }
    const isRevertStartDate =
      new Date(props.data.startDate).getTime() >=
      new Date(props.data.endDate).getTime();
    let announcement = {
      ...props.data,
      startDate: isRevertStartDate
        ? props.data.endDate
        : props.data.startDate,
      endDate: isRevertStartDate ? props.data.startDate : props.data.endDate,
      slides: props.data.slides.map((slide, index) => {
        let newSlide = {
          ...slide,
          title: toTrim(slide.title),
          description: toTrim(slide.description),
          slideOrder: index + 1,
        };
        //파일이 있다면 새슬라이드라고 판단 하기 위해 id 삭제(서버에서 id 제공 예정)
        if (
          deleteItem.includes(slide.id as string) ||
          files.some((file) => file.name === slide.fileName)
        ) {
          delete newSlide.id;
          delete newSlide.url;
        }
        return newSlide;
      }),
      isActive: props.data.id ? props.data.isActive : true,
    };
    if (props.data.id) {
      announcement.deleted = deleteItem;
    }
    delete announcement.isLive;
    let formData = new FormData();
    formData.append("announcement", JSON.stringify(announcement));
    if ((files || []).length === 0) {
      formData.append("files", new Blob([]));
    } else {
      (files || []).forEach((file) => {
        formData.append("files", file);
      });
    }

    if (props.data.id) {
      updateMutation.mutate({ id: props.data.id, payload: formData });
    } else {
      createMutation.mutate(formData);
    }
  };
  const onChangeImage = (id: string, file: File) => {
    const targetSlide = props.data.slides.find((slide) => slide.id === id);
    const isExistingSlide = targetSlide && targetSlide.url !== "";

    // 기존 슬라이드인 경우 삭제 목록에 추가
    if (isExistingSlide) {
      setDeleteItem((prev) => [...prev, id]);
    }

    //미리보기 이미지 만들기
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      // 파일 확장자 추출
      const fileExtension = file.name.split(".").pop();
      const fileName = file.name.split(".").slice(0, -1).join(".");
      // 새로운 파일명 생성: {원본파일명}_{timestamp}.{extension}
      const newFileName = `${fileName}_${new Date().getTime()}.${fileExtension}`;
      // 새로운 File 객체 생성 (name 속성은 읽기 전용이므로)
      const renamedFile = new File([file], newFileName, { type: file.type });

      setFiles((prev) => {
        if (isExistingSlide) {
          // 기존 슬라이드 수정: 기존 파일을 새 파일로 교체
          const existingIndex = prev.findIndex(
            (f) =>
              props.data.slides.find((slide) => slide.id === id)?.fileName ===
              f.name
          );
          if (existingIndex !== -1) {
            prev[existingIndex] = renamedFile;
          } else {
            prev.push(renamedFile);
          }
        } else {
          // 새 슬라이드 추가
          prev.push(renamedFile);
        }
        return prev;
      });

      props.setData({
        ...props.data,
        slides: props.data.slides.map((slide) =>
          slide.id === id
            ? { ...slide, url: imageUrl, fileName: renamedFile.name }
            : slide
        ),
      });
    };
    reader.readAsDataURL(file);
  };

  const onDeleteSlide = (id: string) => {
    const targetSlide = props.data.slides.find((slide) => slide.id === id);
    const isExistingSlide = targetSlide && targetSlide.url !== "";

    // 기존 슬라이드인 경우 삭제 목록에 추가
    if (isExistingSlide) {
      setFiles((prev) =>
        prev.filter((file) => file.name !== targetSlide?.fileName)
      );
      setDeleteItem((prev) => [...prev, id]);
    }
    props.setData({
      ...props.data,
      slides: props.data.slides.filter((slide) => slide.id !== id),
    });
  };

  const mutationDeleteFeaturesPopup = useStatusMutation(deleteFeaturesPopup, {
    onSuccess: () => {
      notifySuccess(lang.label.features.notify.delete.success);
      props.onModal();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(
        err,
        lang.label.features.notify.delete.fail,
        "label.features.error"
      );
    },
  });

  const onClickDelete = () => {
    if (!mutationDeleteFeaturesPopup.idle) return;
    setShowDeleteConfirmModal(!showDeleteConfirmModal);
  };
  const onConfirmDelete = () => {
    setShowDeleteConfirmModal(false);
    if (!mutationDeleteFeaturesPopup.idle) return;
    mutationDeleteFeaturesPopup.mutate(props.data.id as string);
  };

  const onClickClose = () => {
    props.setData({
      ...initialData,
    });
    setFiles([]);
    setDeleteItem([]);
    setValidateMsg({
      ...initialValidateMsg,
    });
    props.onModal();
  };
  return (
    <AddFeaturesPopupPresenter
      onClose={onClickClose}
      data={props.data}
      onChangeFeatures={onChangeFeatures}
      onChangeSlides={onChangeSlides}
      onClickAddSlide={onClickAddSlide}
      onClickPreview={onClickPreview}
      onClickSave={onClickSave}
      onChangeImage={onChangeImage}
      onDeleteSlide={onDeleteSlide}
      validateMsg={validateMsg}
      onClickDelete={onClickDelete}
      showDeleteConfirmModal={showDeleteConfirmModal}
      onConfirmDelete={onConfirmDelete}
      deleteDisabled={!mutationDeleteFeaturesPopup.idle}
      disabledSave={!createMutation.idle || !updateMutation.idle}
    />
  );
}
